import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import InvestorPortalLayout from '../../../components/market/InvestorPortalLayout';
import { useUWMatrix, GuideVersionLine } from '../../../components/UWGuideContent';
import { licensedStatesShort } from '../../../constants/marketExpansion';
import { Icon } from '../../../utils/icons';
import RatePricingSimulator from '../../../components/RatePricingSimulator';
import { useInvestorAuth } from '../../../context/InvestorAuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const PRIMARY = '#3a6ad9';

const SectionCard = ({ children, style }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 22, ...style }}>
    {children}
  </div>
);

// Section pill is the heading itself — the redundant h2 was dropped per
// design feedback. Padding + font bumped slightly so the pill carries
// enough weight to read as a section divider on its own.
const SectionHeading = ({ badge }) => (
  <div style={{ marginBottom: 8 }}>
    {badge && (
      <span style={{ display: 'inline-block', fontSize: 12, fontWeight: 700, color: PRIMARY, background: '#eff3fe', padding: '5px 12px', borderRadius: 999, letterSpacing: 0.4, textTransform: 'uppercase' }}>
        {badge}
      </span>
    )}
  </div>
);

const Card = ({ iconName, title, description, children, button }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, display: 'flex', flexDirection: 'column' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
      <span style={{ width: 36, height: 36, borderRadius: 8, background: '#eff3fe', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={iconName} size={18} color={PRIMARY} />
      </span>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111111' }}>{title}</h3>
    </div>
    {description && (
      <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>{description}</p>
    )}
    {children}
    {button && (
      <div style={{ marginTop: 'auto', paddingTop: 12 }}>{button}</div>
    )}
  </div>
);

const PRIMARY_BTN_STYLE = { padding: '8px 14px', background: PRIMARY, border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: '#fff', textDecoration: 'none', display: 'inline-block' };
const SECONDARY_BTN_STYLE = { padding: '8px 14px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151', textDecoration: 'none', display: 'inline-block' };

// KVTable removed alongside the Section B PMI cards — it was the only
// consumer. Re-add when another tabular K/V surface needs it.

export default function InvestorResources() {
  const [matrixOpen, setMatrixOpen] = useState(false);
  const [processOpen, setProcessOpen] = useState(false);

  return (
    <InvestorPortalLayout>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#111111' }}>Investor Resources</h1>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>
            Reference materials, program documentation, and analytical tools for DoorlyMarket investors.
          </p>
        </div>

        {/* ── Section A: Program Documentation ── */}
        <SectionHeading badge="A · Program Documentation">Program Documentation</SectionHeading>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 14, marginTop: 14, marginBottom: 32 }}>
          <Card
            iconName="book"
            title="Doorly AFM™ Underwriting Guidelines"
            description="Full underwriting guidelines for the Ability-First Mortgage™ program including eligibility, income documentation, asset requirements, and program matrix."
            button={
              <Link to="/market/portal/resources/underwriting-guide" style={PRIMARY_BTN_STYLE}>
                View Guidelines →
              </Link>
            }
          />
          <Card
            iconName="chart"
            title="AFM™ Program Matrix"
            description="LTV, DTI, reserve, and FICO requirements by loan amount tier for the Doorly AFM™ program."
            button={<button onClick={() => setMatrixOpen(true)} style={PRIMARY_BTN_STYLE}>View Matrix →</button>}
          />
          <Card
            iconName="folder"
            title="Sample Loan Tape"
            description="Representative sample of 25 Doorly-originated AFM™ loans showing pool characteristics, FICO distribution, LTV, and geographic concentration."
            button={
              <Link to="/market/sample-tape" target="_blank" rel="noopener noreferrer" style={PRIMARY_BTN_STYLE}>
                View Sample Tape →
              </Link>
            }
          />
          <Card
            iconName="refresh"
            title="Doorly Origination Process"
            description="Step-by-step overview of how Doorly originates, documents, and services AFM™ loans from application through closing."
            button={<button onClick={() => setProcessOpen(true)} style={PRIMARY_BTN_STYLE}>View Overview →</button>}
          />
        </div>

        {/* Section B (PMI & Mortgage Insurance) removed in the MI removal
            pass — Doorly's AFM™ is non-QM and doesn't bind MI on any
            loan, regardless of LTV. Subsequent sections renumbered:
            Rate Pricing (was C) -> B, SmartStart Completion (was C+) ->
            B+, Compliance & Regulatory (was D) -> C. */}

        {/* ── Section B: Rate Simulator ── */}
        <SectionHeading badge="B · Rate Pricing">AFM™ Rate & Payment Simulator</SectionHeading>
        <SectionCard style={{ marginTop: 14, marginBottom: 32 }}>
          <p style={{ margin: '0 0 18px', fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>
            Estimate the rate and monthly payment for a Doorly AFM™ loan based on loan parameters. For indicative purposes only — actual rates subject to credit approval.
          </p>
          <RatePricingSimulator />
        </SectionCard>

        {/* ── SmartStart completion rate ── */}
        <SectionHeading badge="B+ · SmartStart™ Completion">SmartStart™ Completion</SectionHeading>
        <SectionCard style={{ marginTop: 14, marginBottom: 32 }}>
          <SmartStartCompletionStat />
        </SectionCard>

        {/* ── Section C: Compliance ── */}
        <SectionHeading badge="C · Compliance & Regulatory">Compliance & Regulatory</SectionHeading>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap: 14, marginTop: 14, marginBottom: 32 }}>
          <Card
            iconName="document"
            title="TRID Compliance Overview"
            description="All Doorly AFM™ loans are originated in compliance with TRID (TILA-RESPA Integrated Disclosure) requirements, including Loan Estimate and Closing Disclosure delivery timelines."
            button={
              <a href="https://www.consumerfinance.gov/compliance/compliance-resources/mortgage-resources/trid-integrated-disclosure-faqs/"
                 target="_blank" rel="noopener noreferrer" style={SECONDARY_BTN_STYLE}>
                View CFPB TRID Guidelines ↗
              </a>
            }
          />
          <Card
            iconName="check-circle"
            title="ATR / QM Status"
            description="Doorly AFM™ loans are originated under the Ability-to-Repay (ATR) standard as non-QM loans. Each loan file includes documented income, employment, assets, and a good-faith ATR determination per Regulation Z."
          />
          <Card
            iconName="scale"
            title="ECOA / Fair Lending"
            description="Doorly underwrites all applications in compliance with the Equal Credit Opportunity Act (ECOA) and Fair Housing Act. Adverse action notices are issued in compliance with ECOA and FCRA requirements."
          />
          <Card
            iconName="building"
            title="State Licensing"
            description="Go Doorly, LLC d/b/a Doorly. NMLS Company ID #2764749."
          >
            <div className="scroll-x" style={{ background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 8, padding: '10px 12px', marginTop: 4 }}>
              <table style={{ width: '100%', minWidth: 360, borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '6px 8px 6px 0', color: '#6b7280', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.04 }}>State</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.04 }}>License Type</th>
                    <th style={{ textAlign: 'left', padding: '6px 0', color: '#6b7280', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.04 }}>License Number</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '6px 8px 6px 0', color: '#111111' }}>Texas</td>
                    <td style={{ padding: '6px 8px', color: '#374151' }}>Mortgage Company</td>
                    <td style={{ padding: '6px 0', color: '#374151' }}>NMLS #2764749</td>
                  </tr>
                  {/* ⚠️ FLORIDA IS PENDING AND MUST SAY SO. This row listed a
                      Florida Mortgage Broker license as current, with no
                      qualifier, on a page shown to investors — while the
                      internal manual said Florida licensure was in process and
                      incomplete. An unqualified license row is a representation,
                      not a roadmap.

                      Source of truth: backend/constants/marketExpansion.js
                      LICENSES. No import path reaches it from here, so this is a
                      checked copy — flip it in the same commit as that file. */}
                  <tr style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '6px 8px 6px 0', color: '#111111' }}>Florida</td>
                    <td style={{ padding: '6px 8px', color: '#374151' }}>Mortgage Broker — <strong>application pending</strong></td>
                    <td style={{ padding: '6px 0', color: '#9ca3af' }}>Not yet issued</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>

      {matrixOpen && <MatrixModal onClose={() => setMatrixOpen(false)} />}
      {processOpen && <ProcessModal onClose={() => setProcessOpen(false)} />}
    </InvestorPortalLayout>
  );
}

function MatrixModal({ onClose }) {
  // 🔑 AFM v2 (2026-10-21): the program tiers come from afmMatrix.guidePayload(), like every guide surface.
  const matrix = useUWMatrix();
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, maxWidth: 720, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#111111' }}>Doorly AFM™ Program Matrix</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>LTV, DTI, FICO, and reserve requirements by loan tier.</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af' }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px', overflowY: 'auto' }}>
         <div className="scroll-x">
          <table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Loan Amount', 'Max LTV', 'Max DTI', 'Min FICO', 'Reserves'].map((h) => (
                  <th key={h} style={{ background: '#111111', color: '#fff', padding: '10px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.04 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(matrix?.tiers || []).map((t, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? '#ffffff' : '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '8px 12px', color: '#111111', fontWeight: 600 }}>{t.loan}</td>
                  <td style={{ padding: '8px 12px', color: '#374151' }}>{t.ltv}</td>
                  <td style={{ padding: '8px 12px', color: '#374151' }}>{t.dti}</td>
                  <td style={{ padding: '8px 12px', color: '#374151' }}>{t.fico}</td>
                  <td style={{ padding: '8px 12px', color: '#374151' }}>{t.reserves}</td>
                </tr>
              ))}
            </tbody>
          </table>
         </div>
         <div className="scroll-hint">← Scroll to see more →</div>
          <p style={{ margin: '14px 0 0', fontSize: 11, color: '#6b7280', lineHeight: 1.6 }}>
            All loans 30-year fixed-rate, fully amortizing, no prepayment penalty. Owner-occupied primary residence purchases only.
            {` Currently available in ${licensedStatesShort()}.`}
          </p>
          {matrix ? <GuideVersionLine matrix={matrix} style={{ marginTop: 8 }} /> : null}
        </div>
      </div>
    </div>
  );
}

function ProcessModal({ onClose }) {
  const steps = [
    { n: 1, t: 'Application & Verification',          d: 'Borrower applies through the Doorly portal. Income, assets, credit, and identity are verified through Persona, integrated bank connectivity, and a tri-merge credit pull.' },
    { n: 2, t: 'Review & Approval',                   d: "Atlas (Doorly's AI-powered pricing engine) prices the loan based on FICO, LTV, DTI, assets, and reserves. SmartStart™ enrollment is triggered automatically for first-time homebuyers or borrowers with qualifying FICO below 620. SmartEstimate generates an indicative rate and monthly payment." },
    { n: 3, t: 'Property Selection',                  d: 'Borrower shops for an eligible property within their approved parameters. Doorly, through Doorly Realty, LLC, submits an offer to acquire the property. On acceptance by the listing agent, the contract purchase price is locked and the Borrower PSA is routed for execution.' },
    { n: 4, t: 'Loan Estimate & Initial Disclosures', d: 'TRID-compliant Loan Estimate delivered within 3 business days of a property being selected. Initial disclosure package includes Borrower Rights, ECOA appraisal notice, GLBA privacy notice, and Doorly Transaction Services Disclosure. Borrower acknowledges receipt and confirms intent to proceed electronically.' },
    { n: 5, t: 'Closing',                              d: 'Appraisal and inspection ordering (Inspectify), title commitment, hazard insurance verification. All additional needs are cleared prior to advancing. SmartStart™ must be completed before closing if required.' },
    { n: 6, t: 'Clear to Close',                       d: 'Closing Disclosure delivered no later than 3 business days before closing. All CTC gates confirmed: appraisal approved, title cleared, insurance verified, funds confirmed, compliance review complete, and all conditions cleared. Loan automatically advances to Clear to Close when all gates are satisfied. Borrower receives wire instructions and completes e-closing authorization.' },
    { n: 7, t: 'A-B / B-C Same-Day Close',             d: 'Doorly acquires the property from the seller (A-B transaction) using its acquisition credit facility. Simultaneously, Doorly resells the property to the qualifying borrower at the B-C contract price. The promissory note and deed of trust execute at the same closing. Borrower takes title as a first-lien owner-occupied mortgagor.' },
    { n: 8, t: 'Servicing & Secondary Market',         d: "Loan is boarded with Doorly's sub-servicer post-closing. Eligible funded loans are automatically staged into DoorlyMarket pools per the loan tape criteria. Institutional investors access pools through the DoorlyMarket portal." },
  ];
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, maxWidth: 760, width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#111111' }}>Doorly Origination Process</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Application through closing — eight stages.</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#9ca3af' }}>✕</button>
        </div>
        <div style={{ padding: '20px 24px', overflowY: 'auto' }}>
          {steps.map((s) => (
            <div key={s.n} style={{ display: 'flex', gap: 16, paddingBottom: 16, marginBottom: 16, borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 999, background: PRIMARY, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 800 }}>{s.n}</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#111111', marginBottom: 4 }}>{s.t}</div>
                <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>{s.d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// SmartStart completion rate — pulled live from /api/market/smartstart-stats.
// When the platform has zero enrolled borrowers (early state), the card
// renders the static spec copy: "100% — Program completion required prior
// to closing."
function SmartStartCompletionStat() {
  const { token } = useInvestorAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/api/market/smartstart-stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStats(r.data))
      .catch(() => setStats({ total: 0, completed: 0, completionRate: null }));
  }, [token]);

  const hasData = stats && stats.total > 0 && stats.completionRate != null;
  const rate = hasData ? `${stats.completionRate}%` : '100%';
  const sub = hasData
    ? `Across ${stats.total} enrolled borrowers (${stats.completed} completed).`
    : 'Program completion required prior to closing.';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
        <span style={{ fontSize: 48, fontWeight: 800, color: '#111111', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{rate}</span>
        <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 600 }}>SmartStart™ completion rate</span>
      </div>
      <p style={{ margin: '0 0 14px', fontSize: 14, color: '#374151', lineHeight: 1.7 }}>
        100% of enrolled borrowers complete the Homebuyer Success Program prior to closing.
      </p>
      <p style={{ margin: '0 0 14px', fontSize: 14, color: '#374151', lineHeight: 1.7 }}>
        All first-time homebuyers and credit-challenged borrowers (qualifying FICO below 620) are
        automatically enrolled. Program completion is a closing requirement — no exceptions.
      </p>
      <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
        {sub}
      </p>
    </div>
  );
}
